/**
 * Coverage Audit Store
 *
 * Zustand store backing the superadmin Coverage Audit UI.
 *
 * Responsibilities:
 *   1. List recent encounters with their open-issue count.
 *   2. Load issues for a given encounter (filterable by status).
 *   3. Trigger the coverage audit pipeline on demand ("Run audit").
 *   4. Submit reviewer feedback on an issue (and refresh the encounter's
 *      issue list afterward).
 *   5. Track loading + submission state for the UI.
 *
 * Auth: uses the project-wide `apiService` (axios instance with token
 * interceptors) — no need to pass tokens around. Auth is read from
 * localStorage by `apiService` itself; see `frontend/src/services/api.ts`.
 *
 * Backend routes are mounted at `/api/v1/coverage-audit` and the project's
 * `API_BASE_URL` already includes the `/api` prefix, so paths here are
 * `/v1/coverage-audit/...`.
 */
import { create } from 'zustand';
import { apiService } from '../services/api';

// ============================================================================
// SHARED TYPES (mirror the backend enums in
// `backend/src/services/clinical/coverageAudit.types.ts`).
// ============================================================================

export type IssueFamily =
  | 'missing_clinical_fact'
  | 'procedure_capture_gap'
  | 'incomplete_medication_detail'
  | 'wrong_section_assignment'
  | 'unsupported_assertion'
  | 'speaker_role_inconsistency'
  | 'chart_conflict'
  | 'followup_or_plan_gap'
  | 'enrichment_suggestion';

export type SignalChannel = 'issue' | 'enrichment';

export type IssueSeverity = 'major' | 'moderate' | 'minor';

export type IssueDisposition =
  | 'ignore'
  | 'log_only'
  | 'surface_warning'
  | 'rerun_focused'
  | 'auto_patch'
  | 'require_human_review';

export type IssueStatus =
  | 'open'
  | 'auto_resolved'
  | 'needs_review'
  | 'confirmed'
  | 'dismissed';

export type IssueReviewAction = 'accepted' | 'rejected' | 'edited' | 'ignored';

export interface EvidenceSpan {
  source: 'transcript' | 'summary' | 'chart';
  segmentOrderStart?: number;
  segmentOrderEnd?: number;
  section?: string;
  text: string;
}

/**
 * Server-derived repair proposal mirroring the backend
 * `RepairProposal` type. Populated by the listing endpoint so the FE
 * doesn't need to re-derive auto-applicable proposals from
 * attribute-less issue rows. Optional — null when no template matches
 * the issue.
 */
export interface ServerRepairProposal {
  kind:
    | 'replace_text'
    | 'set_section_content'
    | 'clear_section'
    | 'add_to_section'
    | 'manual'
    | 'unsupported';
  targetSide: 'note';
  targetSection: string | null;
  headline: string;
  rationale?: string;
  current?: string;
  proposed?: string;
  replaceFrom?: string;
  replaceTo?: string;
  autoApplicable: boolean;
}

export interface IssueRow {
  id: string;
  encounterId: string;
  family: IssueFamily;
  /**
   * Output channel of the audit pipeline:
   *   - 'issue': defensible clinical errors / omissions (default).
   *   - 'enrichment': constructive rewrite suggestions; never gates anything.
   * The backend defaults missing values to 'issue' for backwards compat
   * with rows persisted before the channel column landed.
   */
  channel?: SignalChannel;
  status: IssueStatus;
  severity: IssueSeverity;
  disposition: IssueDisposition;
  confidence: number;
  reasonCodes: string[];
  evidenceBundle: EvidenceSpan[];
  rationaleShort: string;
  signalIds: string[];
  runId: string;
  createdAt: string;
  updatedAt: string;
  // Server-derived proposal (Phase 2). Optional; null when no template
  // matches. The FE prefers this when set, falling back to its own
  // client-side derivation for families that don't need signal
  // attributes (manual / unsupported templates).
  proposal?: ServerRepairProposal | null;
  // Phase 3 — aggregate detector trust for this issue. Null when no
  // contributing detector has feedback yet. The FE renders this as a
  // small confidence badge so auditors learn to weigh signals from
  // historically-noisy detectors more skeptically.
  trust?: {
    aggregateScore: number;
    minScore: number;
    totalDecisions: number;
    detectorCount: number;
  } | null;
  // Phase 3.7 — audit stage classification. The FE splits the rail by
  // stage so the auditor can mentally separate "our pipeline did this"
  // from "the doctor missed this in the visit".
  stage?: AuditStage;
  // Plain-English single-sentence summary of the policy decision. Replaces
  // the raw reason-codes string the FE used to render to clinicians. Raw
  // codes are still available on `reasonCodes` for engineering debug.
  // See learning-loop spec §5.
  summary?: string;
}

export type AuditStage =
  | 'recording'
  | 'summary_pipeline'
  | 'note_pipeline'
  | 'unknown';

export interface EncounterRow {
  /**
   * The id used for navigation and the existing /v1/clinical/encounter/:id
   * endpoints. For connected-mode encounters this is `encounters.id`; for
   * standalone (appointment-based) encounters it's the appointment id.
   * Null when neither is set (defensive — should not happen in practice).
   */
  id: string | null;
  /** clinical_encounters.id — used for coverage-audit issues and feedback. */
  clinicalEncounterId: string;
  /** Source mode of the encounter — drives which detail route to use. */
  mode: 'connected' | 'standalone';
  date: string;
  patientName?: string;
  encounterType?: string;
  status: string;
  openIssueCount: number;
}

// ============================================================================
// API RESPONSE SHAPES (loosely-typed where the upstream contract is unstable;
// strict where the backend response is well-defined).
// ============================================================================

interface ListIssuesResponse {
  issues: IssueRow[];
  runId: string | null;
}

/**
 * Per-run debug payload (Phase 1.4). Populated by the backend on every
 * /run response. Counts only — no PHI. Used by the detail page to render
 * an inline breakdown of what each detector contributed without
 * forcing the auditor to grep server logs.
 */
export interface RunPipelineDebug {
  inputs: {
    transcriptLen: number;
    transcriptLooksBase64Recovered: boolean;
    summaryLen: number;
    summaryShape: 'object' | 'string' | 'absent';
    medicalNoteLoaded: boolean;
    medicalNoteFormat: string | null;
  };
  tah: {
    transcriptEntities: number;
    transcriptRelations: number;
    summaryEntities: number;
    summaryRelations: number;
  };
  detectors: {
    tahSignals: number;
    llmJudgeSignals: number;
    llmEnrichmentsSignals: number;
    standardOfCareSignals: number;
    noteDriftSignals: number;
    noteEvidenceEmptySignals: number;
    noteSectionRelocationSignals: number;
    medicationSignals: number;
    medicationCandidatesChecked: number;
    medicationRxNormVerified: number;
    medicationRxNormNoMatch: number;
    medicationRxNormErrors: number;
    medicationImoVerified: number;
    medicationImoNoMatch: number;
    medicationImoErrors: number;
    numericSignals: number;
  };
  output: {
    totalSignals: number;
    candidates: number;
    issues: number;
    signalsPersisted: number;
    byFamily: Record<string, number>;
    durationMs: number;
  };
}

interface RunPipelineResponse {
  runId: string;
  signalsPersisted: number;
  issues: IssueRow[];
  debug?: RunPipelineDebug;
}

interface SubmitFeedbackResponse {
  issue: IssueRow;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface EncountersListResponse {
  items?: EncounterRow[];
  // Back-compat: the previous shape returned `encounters`. Keep parsing it
  // until the backend stops emitting the alias.
  encounters?: EncounterRow[];
  pagination?: PaginationMeta;
}

export type CoverageAuditStatusFilter = 'completed' | 'in_progress' | 'all';

export interface ListEncountersFilters {
  page?: number;
  limit?: number;
  status?: CoverageAuditStatusFilter;
  search?: string;
  withIssues?: boolean;
  dateFrom?: string;
  dateTo?: string;
}

// ============================================================================
// STORE
// ============================================================================

interface CoverageAuditState {
  // Encounter list (Recent encounters tab) — server-side paginated and
  // filtered. `loadEncounters` accepts the full filter set; the page owns
  // the URL state and feeds it in.
  encounters: EncounterRow[];
  encountersPagination: PaginationMeta;
  encountersLoading: boolean;
  encountersError: string | null;
  loadEncounters: (filters?: ListEncountersFilters) => Promise<void>;

  // Issues by encounter (Encounter detail page)
  encounterIssues: Record<string, IssueRow[]>;
  encounterIssuesLoading: boolean;
  encounterIssuesError: string | null;
  loadEncounterIssues: (
    encounterId: string,
    status?: IssueStatus,
  ) => Promise<void>;

  // Last run's debug payload — keyed by encounter so the detail page can
  // surface a breakdown panel after Re-run audit completes. Cleared on
  // explicit reset only; not refreshed by `loadEncounterIssues` since that
  // GET endpoint doesn't return debug data.
  lastRunDebug: Record<string, RunPipelineDebug>;

  // On-demand pipeline run ("Run audit" button)
  runPipeline: (
    encounterId: string,
  ) => Promise<{ runId: string; issues: IssueRow[]; debug?: RunPipelineDebug }>;

  // Reviewer feedback (per issue)
  flagSubmitting: boolean;
  flagError: string | null;
  submitFeedback: (args: {
    issueId: string;
    encounterId: string;
    reviewAction: IssueReviewAction;
    resolutionReason: string;
    correctedContent?: Record<string, unknown>;
  }) => Promise<void>;

  // Phase 2.3 — apply a repair proposal. Backend re-derives the proposal
  // server-side, mutates the medical note inside a transaction, records
  // audit + feedback rows, and marks the issue confirmed. After the
  // round-trip we refresh the encounter's issue list so the rail
  // reflects the new state.
  applyRepairLoading: Record<string, boolean>;
  applyRepairError: Record<string, string | null>;
  applyRepair: (args: {
    issueId: string;
    encounterId: string;
  }) => Promise<{ noteId: string }>;
}

// Helper: pull a human-readable error message out of an axios error or
// generic JS error.
function extractError(error: unknown, fallback: string): string {
  const err = error as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };
  return (
    err.response?.data?.error ||
    err.response?.data?.message ||
    err.message ||
    fallback
  );
}

export const useCoverageAuditStore = create<CoverageAuditState>((set, get) => ({
  // --------------------------------------------------------------------------
  // ENCOUNTERS LIST
  // --------------------------------------------------------------------------
  encounters: [],
  encountersPagination: { page: 1, limit: 50, total: 0, pages: 1 },
  encountersLoading: false,
  encountersError: null,

  loadEncounters: async (filters = {}) => {
    set({ encountersLoading: true, encountersError: null });
    try {
      // Server-side paginated + filtered. The backend evaluates artifact-
      // based status, search, date range, and "with open issues" once per
      // request and returns just the page the UI needs plus the total so
      // the pager can render `Showing X–Y of Z`.
      const qs = new URLSearchParams();
      qs.set('page', String(filters.page ?? 1));
      qs.set('limit', String(filters.limit ?? 50));
      qs.set('status', filters.status ?? 'completed');
      if (filters.search?.trim()) qs.set('search', filters.search.trim());
      if (filters.withIssues) qs.set('withIssues', 'true');
      if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) qs.set('dateTo', filters.dateTo);

      const data = await apiService.get<EncountersListResponse>(
        `/v1/clinical/encounters?${qs.toString()}`,
      );
      const items = data.items ?? data.encounters ?? [];
      const pagination = data.pagination ?? {
        page: filters.page ?? 1,
        limit: filters.limit ?? 50,
        total: items.length,
        pages: 1,
      };
      set({
        encounters: items,
        encountersPagination: pagination,
        encountersLoading: false,
      });
    } catch (error) {
      set({
        encountersLoading: false,
        encountersError: extractError(error, 'Failed to load encounters'),
      });
    } finally {
      if (get().encountersLoading) set({ encountersLoading: false });
    }
  },

  // --------------------------------------------------------------------------
  // ENCOUNTER ISSUES
  // --------------------------------------------------------------------------
  encounterIssues: {},
  encounterIssuesLoading: false,
  encounterIssuesError: null,

  loadEncounterIssues: async (encounterId, status) => {
    set({ encounterIssuesLoading: true, encounterIssuesError: null });
    try {
      // Request severity-ordered results from the backend so the UI doesn't
      // have to re-sort. We don't pass `limit` here — the page renders top-5
      // per channel client-side because we want both stacks balanced even
      // when one channel is silent.
      const params = new URLSearchParams({ orderBy: 'severity' });
      if (status) params.set('status', status);
      const url = `/v1/coverage-audit/encounters/${encounterId}/issues?${params.toString()}`;
      const data = await apiService.get<ListIssuesResponse>(url);
      set((s) => ({
        encounterIssues: { ...s.encounterIssues, [encounterId]: data.issues },
        encounterIssuesLoading: false,
      }));
    } catch (error) {
      set({
        encounterIssuesLoading: false,
        encounterIssuesError: extractError(error, 'Failed to load issues'),
      });
    } finally {
      if (get().encounterIssuesLoading) {
        set({ encounterIssuesLoading: false });
      }
    }
  },

  // --------------------------------------------------------------------------
  // RUN PIPELINE
  // --------------------------------------------------------------------------
  lastRunDebug: {},

  runPipeline: async (encounterId) => {
    const data = await apiService.post<RunPipelineResponse>(
      '/v1/coverage-audit/run',
      { encounterId },
    );
    // Cache debug immediately — it only comes from the run response, not
    // from the subsequent list call.
    set((s) => ({
      lastRunDebug: data.debug
        ? { ...s.lastRunDebug, [encounterId]: data.debug }
        : s.lastRunDebug,
    }));
    // Always reload via loadEncounterIssues to pick up the per-issue
    // server-derived metadata (stage, trust, proposal). The /run
    // response is bare PersistedIssue rows without those fields, so
    // relying on it directly leaves the FE blind to stage routing,
    // trust badges, and auto-fix proposals. The extra round-trip is
    // <100ms and runs in parallel with the user's first read of the
    // updated rail.
    await get().loadEncounterIssues(encounterId);
    return { runId: data.runId, issues: data.issues, debug: data.debug };
  },

  // --------------------------------------------------------------------------
  // REVIEWER FEEDBACK
  // --------------------------------------------------------------------------
  flagSubmitting: false,
  flagError: null,

  applyRepairLoading: {},
  applyRepairError: {},

  applyRepair: async (args) => {
    set((s) => ({
      applyRepairLoading: { ...s.applyRepairLoading, [args.issueId]: true },
      applyRepairError: { ...s.applyRepairError, [args.issueId]: null },
    }));
    try {
      const data = await apiService.post<{
        success: boolean;
        issueId: string;
        noteId: string;
        appliedAt: string;
      }>(`/v1/coverage-audit/issues/${args.issueId}/repair`, {});
      // Refresh the encounter's issue list so the just-applied issue
      // moves to status=confirmed and disappears from the open rail.
      await get().loadEncounterIssues(args.encounterId);
      return { noteId: data.noteId };
    } catch (error) {
      const message = extractError(error, 'Failed to apply repair');
      set((s) => ({
        applyRepairError: { ...s.applyRepairError, [args.issueId]: message },
      }));
      throw error;
    } finally {
      set((s) => ({
        applyRepairLoading: { ...s.applyRepairLoading, [args.issueId]: false },
      }));
    }
  },

  submitFeedback: async (args) => {
    set({ flagSubmitting: true, flagError: null });
    try {
      await apiService.post<SubmitFeedbackResponse>(
        `/v1/coverage-audit/issues/${args.issueId}/feedback`,
        {
          reviewAction: args.reviewAction,
          resolutionReason: args.resolutionReason,
          correctedContent: args.correctedContent,
        },
      );
      // Refresh the encounter's issues so the UI reflects the new status
      // (e.g., 'confirmed' / 'dismissed' / 'needs_review').
      await get().loadEncounterIssues(args.encounterId);
    } catch (error) {
      const message = extractError(error, 'Failed to submit feedback');
      set({ flagError: message });
      throw error;
    } finally {
      set({ flagSubmitting: false });
    }
  },
}));
